import { useCallback, useEffect, useState } from 'react'
import { useGetDraftLeadOnMount } from '@user/store/draftLeads'
import { NameValue } from '../../../NameValueList'
import { filterFieldNames, keyNameReplacements } from '../../../constants'
import { useTranslation } from 'react-i18next'

type Props = {
  draftLeadId: number | null
  showNulls?: boolean
}

export function useData(props: Props) {
  const { draftLeadId, showNulls = false } = props
  const [nameValueList, setNameValueList] = useState<NameValue[]>([])
  const { t } = useTranslation()
  const [loading, draft] = useGetDraftLeadOnMount(draftLeadId?.toString())

  function flattenObject(obj, filterNames, type) {
    const flattenedObj = {}

    function stringifyObj(object, propName) {
      if (filterNames.includes(propName)) {
        return
      }
      if (showNulls && propName && !object) {
        flattenedObj[propName] = '-'
        return
      }
      if (typeof object !== 'object') {
        flattenedObj[propName] = object
        return
      }

      for (const prop in object) {
        if (propName === '') stringifyObj(object[prop], prop)
        else {
          if (
            (type === 'ServiceBot' && propName === 'Custom') ||
            propName === 'PublicCustomFieldValues'
          ) {
            stringifyObj(object[prop].Value, object[prop].Name)
          } else {
            stringifyObj(object[prop], prop)
          }
        }
      }
    }

    stringifyObj(obj, '')
    return flattenedObj
  }

  const replaceKey = useCallback(
    (s) => {
      return keyNameReplacements(t).find((x) => x.oldKey === s)?.newKey ?? s
    },
    [t],
  )

  const mapWebformDataToNameValueList = useCallback((draft) => {
    const list: NameValue[] = []

    if (draft?.initialDraft) {
      const flattened = flattenObject(
        draft.initialDraft,
        filterFieldNames,
        draft?.type,
      )

      for (const property in flattened) {
        list.push({
          name: replaceKey(property),
          value: flattened[property]?.toString() ?? '(empty)',
        })
      }
    }

    return list
  }, [])

  useEffect(() => {
    if (!loading && draft) {
      setNameValueList(mapWebformDataToNameValueList(draft))
    } else {
      setNameValueList([])
    }
  }, [draft, loading, mapWebformDataToNameValueList])

  return { loading, nameValueList, draft }
}
